import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { clearSsoSession, consumeSsoSession, startSsoLogin } from '../utils/ssoAuth';
import { useAuthStore } from '../stores/auth';

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const completeSession = useAuthStore((s) => s.completeSessionWithToken);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const session = consumeSsoSession();

    if (!code || !state || !session) {
      setError('Invalid or expired sign-in session. Please try again.');
      return;
    }

    if (state !== session.state) {
      clearSsoSession();
      setError('Sign-in state mismatch. Please try again.');
      return;
    }

    api
      .post('/api/v1/auth/sso/callback', {
        code,
        code_verifier: session.codeVerifier,
      })
      .then(async (response) => {
        clearSsoSession();
        await completeSession(response.data.accessToken);
        navigate(session.returnTo, { replace: true });
      })
      .catch((err) => {
        clearSsoSession();
        const message =
          err?.response?.data?.message ||
          (Array.isArray(err?.response?.data?.message)
            ? err.response.data.message.join(', ')
            : null) ||
          'Sign-in failed';
        setError(message);
      });
  }, [searchParams, navigate, completeSession]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
        <div className="bg-surface-900 border border-surface-700 rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-surface-100 mb-2">Sign-in Failed</h1>
          <p className="text-sm text-surface-400 mb-6">{error}</p>
          <button
            onClick={() => startSsoLogin('/dashboard')}
            className="btn-primary px-6 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
        <p className="text-surface-400 text-sm">Completing sign-in...</p>
      </div>
    </div>
  );
}
